import React from "react";
import { Box, Typography, Container, Stack, Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useTranslation } from "react-i18next";
import { trackEvent } from "src/utils/analytics";
import { useLocale } from "src/i18n/LocaleProvider";
import {
  audiencePath,
  blogPath,
  pricingPath,
  termsPath,
} from "src/i18n/paths";
import LanguageSwitcher from "../LanguageSwitcher";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const footerLinks = [
    { label: t("footer.pricing"), path: pricingPath(locale) },
    { label: t("footer.cities"), path: audiencePath("cities", locale) },
    {
      label: t("footer.localGovernments"),
      path: audiencePath("local-governments", locale),
    },
    { label: t("footer.hoas"), path: audiencePath("hoas", locale) },
    { label: t("footer.blog"), path: blogPath(locale) },
    { label: t("footer.terms"), path: termsPath(locale) },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "#070a10",
        padding: "1.5rem 0",
        borderTop: "1px solid rgba(30, 41, 59, 0.9)",
      }}
    >
      <Container
        sx={{
          display: "flex",
          maxWidth: "100%!important",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "row",
            lg: "row",
          },
          gap: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "#94a3b8",
            flex: 1,
          }}
        >
          {t("footer.questions")}{" "}
          <Box
            component="a"
            href="mailto:customerservice@strmonitoring.com"
            onClick={() =>
              trackEvent("outbound_click", {
                location: "footer",
                destination: "mailto:customerservice@strmonitoring.com",
              })
            }
            sx={{ wordBreak: "break-all" }}
          >
            customerservice@strmonitoring.com
          </Box>
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
          alignItems="center"
          sx={{ flex: 1 }}
        >
          {footerLinks.map((link) => (
            <MuiLink
              key={link.path}
              component={RouterLink}
              to={link.path}
              underline="hover"
              onClick={() =>
                trackEvent("cta_click", {
                  cta_id: "footer_nav",
                  location: "footer",
                  destination: link.path,
                  label: link.label,
                })
              }
              sx={{
                color: "#94a3b8",
                fontSize: "0.875rem",
                "&:hover": { color: "#d8b4fe" },
              }}
            >
              {link.label}
            </MuiLink>
          ))}
          <LanguageSwitcher />
        </Stack>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "#64748b",
            flex: 1,
          }}
        >
          {t("footer.copyright", { year: new Date().getFullYear() })}
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
